import { ButtonHTMLAttributes, AnchorHTMLAttributes, ReactNode } from 'react'
import { Icon, IconName } from '@/components/Icon'

export type ButtonAppearance = 'solid' | 'stroke' | 'subtle' | 'ghost'
export type ButtonColor =
	| 'brand'
	| 'content1'
	| 'content2'
	| 'content3'
	| 'content4'
	| 'success'
	| 'warning'
	| 'danger'
	| 'note'
	| 'overlay'
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg'

const iconSizeMap: Record<ButtonSize, number> = {
	xs: 16,
	sm: 16,
	md: 24,
	lg: 24
}

type ButtonBaseProps = {
	label?: string
	appearance?: ButtonAppearance
	color?: ButtonColor
	size?: ButtonSize
	iconOnly?: IconName
	iconLeft?: IconName
	iconRight?: IconName
	spacesClass?: string
}

type ButtonAsButton = ButtonBaseProps &
	ButtonHTMLAttributes<HTMLButtonElement> & { href?: undefined }
type ButtonAsAnchor = ButtonBaseProps &
	AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }

type ButtonProps = ButtonAsButton | ButtonAsAnchor

const sizeClasses: Record<ButtonSize, string> = {
	xs: 'spaces:text-14/16 spaces:gap-4 spaces:px-7 spaces:py-3 spaces:rounded-4 spaces:min-w-24 ',
	sm: 'spaces:text-14/16 spaces:gap-8 spaces:px-11 spaces:py-7 spaces:rounded-8 spaces:min-w-32',
	md: 'spaces:text-16/24 spaces:gap-8 spaces:px-15 spaces:py-7 spaces:rounded-12 spaces:min-w-40',
	lg: 'spaces:text-16/24 spaces:gap-12 spaces:px-15 spaces:py-11 spaces:rounded-16 spaces:min-w-48'
}

const iconOnlySizeClasses: Record<ButtonSize, string> = {
	xs: 'spaces:p-3 spaces:rounded-4 spaces:min-w-24',
	sm: 'spaces:p-7 spaces:rounded-8 spaces:min-w-32',
	md: 'spaces:p-7 spaces:rounded-12 spaces:min-w-40',
	lg: 'spaces:p-11 spaces:rounded-16 spaces:min-w-48'
}

function getAppearanceClasses (
	appearance: ButtonAppearance,
	color: ButtonColor
): string {
	if (appearance === 'solid') {
		switch (color) {
			case 'brand':
				return 'spaces:bg-brand-base spaces:text-brand-on-base spaces:border-transparent spaces:hover:bg-brand-hover'
			case 'content1':
				return 'spaces:bg-canvas-inverse spaces:text-content-inverse spaces:border-transparent spaces:hover:bg-canvas-2 spaces:hover:text-content-1'
			case 'content2':
				return 'spaces:bg-canvas-inverse spaces:text-content-inverse spaces:border-transparent spaces:hover:bg-canvas-2 spaces:hover:text-content-1'
			case 'content3':
				return 'spaces:bg-canvas-inverse spaces:text-content-inverse spaces:border-transparent spaces:hover:bg-canvas-2 spaces:hover:text-content-1'
			case 'content4':
				return 'spaces:bg-canvas-inverse spaces:text-content-inverse spaces:border-transparent spaces:hover:bg-canvas-2 spaces:hover:text-content-1'
			case 'success':
				return 'spaces:bg-success-base spaces:text-success-on-base spaces:border-transparent spaces:hover:bg-success-hover'
			case 'warning':
				return 'spaces:bg-warning-base spaces:text-warning-on-base spaces:border-transparent spaces:hover:bg-warning-hover'
			case 'danger':
				return 'spaces:bg-danger-base spaces:text-danger-on-base spaces:border-transparent spaces:hover:bg-danger-hover'
			case 'note':
				return 'spaces:bg-note-base spaces:text-note-on-base spaces:border-transparent spaces:hover:bg-note-alt'
			case 'overlay':
				return 'spaces:bg-white/90 spaces:text-black/75 spaces:border-transparent spaces:hover:bg-white/100'
		}
	}
	if (appearance === 'stroke') {
		switch (color) {
			case 'brand':
				return 'spaces:bg-canvas-base spaces:text-brand-content spaces:border-brand-base spaces:hover:bg-brand-base spaces:hover:text-brand-on-base spaces:hover:border-transparent'
			case 'content1':
				return 'spaces:bg-canvas-base spaces:text-content-1 spaces:border-canvas-2 spaces:hover:bg-canvas-4'
			case 'content2':
				return 'spaces:bg-canvas-base spaces:text-content-2 spaces:border-canvas-2 spaces:hover:bg-canvas-4'
			case 'content3':
				return 'spaces:bg-canvas-base spaces:text-content-3 spaces:border-canvas-2 spaces:hover:bg-canvas-4'
			case 'content4':
				return 'spaces:bg-canvas-base spaces:text-content-4 spaces:border-canvas-2 spaces:hover:bg-canvas-4'
			case 'success':
				return 'spaces:bg-canvas-base spaces:text-success-content spaces:border-success-base spaces:hover:bg-success-base spaces:hover:text-success-on-base spaces:hover:border-transparent'
			case 'warning':
				return 'spaces:bg-canvas-base spaces:text-warning-content spaces:border-warning-base spaces:hover:bg-warning-base spaces:hover:text-warning-on-base spaces:hover:border-transparent'
			case 'danger':
				return 'spaces:bg-canvas-base spaces:text-danger-content spaces:border-danger-base spaces:hover:bg-danger-base spaces:hover:text-danger-on-base spaces:hover:border-transparent'
			case 'note':
				return 'spaces:bg-canvas-base spaces:text-note-content spaces:border-note-base spaces:hover:bg-note-base spaces:hover:text-note-on-base spaces:hover:border-transparent'
			case 'overlay':
				return 'spaces:bg-black/55 spaces:text-white spaces:border-white/70 spaces:hover:bg-white/90 spaces:hover:text-black/75 spaces:hover:border-transparent'
		}
	}
	if (appearance === 'subtle') {
		switch (color) {
			case 'brand':
				return 'spaces:bg-brand-subtle spaces:text-brand-on-subtle spaces:border-transparent spaces:hover:bg-brand-base spaces:hover:text-brand-on-base'
			case 'content1':
				return 'spaces:bg-canvas-4 spaces:text-content-1 spaces:border-transparent spaces:hover:bg-canvas-3'
			case 'content2':
				return 'spaces:bg-canvas-4 spaces:text-content-2 spaces:border-transparent spaces:hover:bg-canvas-3'
			case 'content3':
				return 'spaces:bg-canvas-4 spaces:text-content-3 spaces:border-transparent spaces:hover:bg-canvas-3'
			case 'content4':
				return 'spaces:bg-canvas-4 spaces:text-content-4 spaces:border-transparent spaces:hover:bg-canvas-3 spaces:hover:text-content-3'
			case 'success':
				return 'spaces:bg-success-subtle spaces:text-success-on-subtle spaces:border-transparent spaces:hover:bg-success-base spaces:hover:text-success-on-base'
			case 'warning':
				return 'spaces:bg-warning-subtle spaces:text-warning-on-subtle spaces:border-transparent spaces:hover:bg-warning-base spaces:hover:text-warning-on-base'
			case 'danger':
				return 'spaces:bg-danger-subtle spaces:text-danger-on-subtle spaces:border-transparent spaces:hover:bg-danger-base spaces:hover:text-danger-on-base'
			case 'note':
				return 'spaces:bg-note-subtle spaces:text-note-on-subtle spaces:border-transparent spaces:hover:bg-note-base spaces:hover:text-note-on-base'
			case 'overlay':
				return 'spaces:bg-black/55 spaces:text-white spaces:border-transparent spaces:hover:bg-white/90 spaces:hover:text-black/75'
		}
	}
	// ghost
	switch (color) {
		case 'brand':
			return 'spaces:bg-transparent spaces:text-brand-content spaces:border-transparent spaces:hover:bg-brand-subtle spaces:hover:text-brand-on-subtle'
		case 'content1':
			return 'spaces:bg-transparent spaces:text-content-1 spaces:border-transparent spaces:hover:bg-canvas-4'
		case 'content2':
			return 'spaces:bg-transparent spaces:text-content-2 spaces:border-transparent spaces:hover:bg-canvas-4'
		case 'content3':
			return 'spaces:bg-transparent spaces:text-content-3 spaces:border-transparent spaces:hover:bg-canvas-4'
		case 'content4':
			return 'spaces:bg-transparent spaces:text-content-4 spaces:border-transparent spaces:hover:bg-canvas-4'
		case 'success':
			return 'spaces:bg-transparent spaces:text-success-content spaces:border-transparent spaces:hover:bg-success-subtle spaces:hover:text-success-on-subtle'
		case 'warning':
			return 'spaces:bg-transparent spaces:text-warning-content spaces:border-transparent spaces:hover:bg-warning-subtle spaces:hover:text-warning-on-subtle'
		case 'danger':
			return 'spaces:bg-transparent spaces:text-danger-content spaces:border-transparent spaces:hover:bg-danger-subtle spaces:hover:text-danger-on-subtle'
		case 'note':
			return 'spaces:bg-transparent spaces:text-note-content spaces:border-transparent spaces:hover:bg-note-subtle spaces:hover:text-note-on-subtle'
		case 'overlay':
			return 'spaces:bg-transparent spaces:text-white spaces:border-transparent spaces:hover:bg-white/90 spaces:hover:text-black/75'
	}
}

export function Button ({
	label,
	appearance = 'solid',
	color = 'brand',
	size = 'md',
	iconOnly,
	iconLeft,
	iconRight,
	spacesClass = '',
	className = '',
	children,
	...props
}: ButtonProps) {
	const isIconOnly = iconOnly !== undefined && !label && !children
	const isDisabled = 'disabled' in props && Boolean(props.disabled)
	const iconPx = iconSizeMap[size]

	const classes = [
		'spaces-button spaces:inline-flex spaces:items-center spaces:justify-center spaces:font-default spaces:transition-colors spaces:cursor-pointer spaces:whitespace-nowrap spaces:border-1',
		isDisabled ? 'spaces-disabled' : '',
		'spaces:focus-visible:outline-canvas-base spaces:focus-visible:ring-2 spaces:focus-visible:ring-brand-base spaces:focus-visible:ring-offset-2',
		'spaces:disabled:opacity-disabled spaces:disabled:pointer-events-none spaces:disabled:cursor-not-allowed',
		isIconOnly ? iconOnlySizeClasses[size] : sizeClasses[size],
		getAppearanceClasses(appearance, color),
		spacesClass,
		className
	].join(' ')

	const content = isIconOnly ? (
		<Icon name={iconOnly} size={iconPx} />
	) : (
		<>
			{iconLeft && <Icon name={iconLeft} size={iconPx} />}
			{label && <span>{label}</span>}
			{children}
			{iconRight && <Icon name={iconRight} size={iconPx} />}
		</>
	)

	if ('href' in props && props.href !== undefined) {
		const { href, ...anchorProps } = props as ButtonAsAnchor
		return (
			<a className={classes} href={href} {...anchorProps}>
				{content}
			</a>
		)
	}

	return (
		<button className={classes} {...(props as ButtonAsButton)}>
			{content}
		</button>
	)
}
